import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { axiosCompany, displayDate, mapStateToProps, SpinnerButton } from '../../helpers/Common'
import DeskLogo from '../../assets/images/kerjoo-desk-logo.png'
import DeskLogoDark from '../../assets/images/kerjoo-desk-logo-dark.png'
import { isLangEN, LangResetDataFirst } from '../../constant/languange'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faAt, faCheckCircle, faExternalLinkAlt, faInbox, faInfoCircle, faStickyNote, faTicketAlt } from '@fortawesome/free-solid-svg-icons'

const DESK_DOMAIN = 'desk.kerjoo.id'

const slugify = (name) => (name || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32)

/** Buka Kerjoo Desk lewat SSO (token sekali pakai), dipakai juga oleh app switcher di sidebar. */
export function openKerjooDesk () {
  // Tab dibuka lebih dulu agar tidak diblokir popup blocker (window.open setelah request async)
  const tab = window.open('', '_blank')
  return axiosCompany().get('/integrations/desk/sso-token')
    .then((res) => {
      const { ott_key, desk_app_url } = res.data
      if (tab) tab.location.href = `${desk_app_url}/ott?ott_token=${ott_key}`
      else window.location.href = `${desk_app_url}/ott?ott_token=${ott_key}`
    })
    .catch((err) => {
      if (tab) tab.close()
      throw err
    })
}

// tombol yang diblokir saat data contoh: benar-benar tidak bisa diklik/hover
const blockedStyle = { pointerEvents: 'none', cursor: 'not-allowed', opacity: 0.5 }

const palette = (dark) => ({
  heroBg: dark ? 'linear-gradient(135deg, #272841 0%, #2b2547 100%)' : 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)',
  heroBorder: dark ? '#363759' : '#e0e7ff',
  card: dark ? '#262733' : '#ffffff',
  cardBorder: dark ? '#34354a' : '#ecebf5',
  text: dark ? '#f3f4f6' : '#111827',
  muted: dark ? '#a1a1b5' : '#6b7280',
  accent: dark ? '#818cf8' : '#4f46e5',
  accentSoft: dark ? 'rgba(129, 140, 248, 0.16)' : '#eef2ff',
  input: dark ? '#1f2029' : '#ffffff',
  success: dark ? '#4ade80' : '#15803d',
  successSoft: dark ? 'rgba(74, 222, 128, 0.14)' : '#dcfce7',
  danger: dark ? '#f87171' : '#dc2626'
})

/** Ilustrasi inbox Kerjoo Desk (contoh, bukan data asli). */
function InboxPreview ({ c }) {
  const rows = isLangEN
    ? [
        { from: 'Rina · PT Sinar', subject: 'September invoice not received yet', status: 'Open', color: '#6366f1', who: 'B' },
        { from: 'Andi', subject: 'Re: Delivery schedule change', status: 'Pending', color: '#d97706', who: 'S' },
        { from: 'info@tokobaru.id', subject: 'Request for the latest catalog', status: 'Resolved', color: '#16a34a', who: 'D' }
      ]
    : [
        { from: 'Rina · PT Sinar', subject: 'Invoice September belum diterima', status: 'Open', color: '#6366f1', who: 'B' },
        { from: 'Andi', subject: 'Re: Perubahan jadwal pengiriman', status: 'Pending', color: '#d97706', who: 'S' },
        { from: 'info@tokobaru.id', subject: 'Minta katalog produk terbaru', status: 'Resolved', color: '#16a34a', who: 'D' }
      ]

  return (
    <div aria-hidden style={{ background: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: 14, boxShadow: '0 12px 32px rgba(79, 70, 229, 0.12)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${c.cardBorder}` }}>
        <FontAwesomeIcon icon={faInbox} style={{ color: c.accent }} />
        <span style={{ fontWeight: 600, fontSize: 13, color: c.text }}>support@</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: c.accent, background: c.accentSoft, borderRadius: 999, padding: '2px 8px' }}>
          {isLangEN ? '3 new' : '3 baru'}
        </span>
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: i < rows.length - 1 ? `1px solid ${c.cardBorder}` : 'none' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, color: c.muted, marginBottom: 2 }}>{r.from}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: c.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.subject}</div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 600, color: r.color, background: `${r.color}24`, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>{r.status}</span>
          <span style={{ width: 24, height: 24, borderRadius: '50%', background: c.accentSoft, color: c.accent, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{r.who}</span>
        </div>
      ))}
    </div>
  )
}

function DeveloperKerjooDeskComponent ({ redux }) {
  const dark = !!redux.darkModeOn
  const c = palette(dark)
  const [status, setStatus] = useState(null)
  const [subdomain, setSubdomain] = useState(slugify(redux.company?.name))
  const [availability, setAvailability] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingSso, setLoadingSso] = useState(false)
  const [error, setError] = useState('')
  const timer = useRef()

  // Nama company bisa termuat setelah komponen tampil
  useEffect(() => {
    if (!subdomain && redux.company?.name) setSubdomain(slugify(redux.company.name))
  }, [redux.company?.name])

  useEffect(() => {
    axiosCompany().get('/integrations/desk/status')
      .then((res) => setStatus(res.data))
      .catch(() => setStatus({ is_connected: false, linked: null }))
  }, [])

  // Cek ketersediaan subdomain (hanya bila belum pernah tertaut dengan company Desk)
  useEffect(() => {
    setAvailability(null)
    clearTimeout(timer.current)
    if (!status || status.is_connected || status.linked || subdomain.length < 3) return
    timer.current = setTimeout(() => {
      axiosCompany().get('/integrations/desk/subdomain-available', { params: { subdomain } })
        .then((res) => setAvailability(res.data))
        .catch(() => {})
    }, 400)
    return () => clearTimeout(timer.current)
  }, [subdomain, status])

  const handleConnect = () => {
    if (!canConnect) return
    setLoading(true)
    setError('')
    axiosCompany().post('/integrations/desk/connect', status?.linked ? {} : { subdomain })
      .then(() => {
        window.location.hash = '#/developer?tab=kerjoo-desk'
        window.location.reload()
      })
      .catch((err) => {
        setError(err.response?.data?.message || (isLangEN ? 'Connection failed' : 'Koneksi gagal'))
        setLoading(false)
      })
  }

  const handleSso = () => {
    setLoadingSso(true)
    setError('')
    openKerjooDesk()
      .catch((err) => setError(err.response?.data?.message || (isLangEN ? 'Failed to open Kerjoo Desk' : 'Gagal membuka Kerjoo Desk')))
      .finally(() => setLoadingSso(false))
  }

  const features = [
    {
      icon: faInbox,
      title: isLangEN ? 'One shared inbox' : 'Satu inbox bersama',
      text: isLangEN ? 'support@, cs@, info@ in one place, handled together by your team.' : 'support@, cs@, info@ di satu tempat, ditangani bersama oleh tim.'
    },
    {
      icon: faTicketAlt,
      title: isLangEN ? 'Emails become tickets' : 'Email jadi tiket',
      text: isLangEN ? 'Status, priority, and assignee, so no customer email slips through.' : 'Ada status, prioritas, dan penanggung jawab, tidak ada email pelanggan yang terlewat.'
    },
    {
      icon: faStickyNote,
      title: isLangEN ? 'Faster teamwork' : 'Kerja tim lebih cepat',
      text: isLangEN ? 'Internal notes, reply templates, and automatic spam filtering.' : 'Catatan internal, template balasan, dan filter spam otomatis.'
    },
    {
      icon: faAt,
      title: isLangEN ? 'Your own email address' : 'Alamat email Anda sendiri',
      text: isLangEN ? 'Replies go out from your company address, free of charge.' : 'Balasan terkirim dari alamat email perusahaan Anda, gratis.'
    }
  ]

  const steps = [
    {
      title: isLangEN ? 'Choose an address' : 'Pilih alamat',
      text: isLangEN ? `Your team opens Kerjoo Desk at yourname.${DESK_DOMAIN}.` : `Tim membuka Kerjoo Desk di namaanda.${DESK_DOMAIN}.`
    },
    {
      title: isLangEN ? 'You become the Admin' : 'Anda menjadi Admin',
      text: isLangEN ? 'Connect your mailbox (IMAP/SMTP) from the Kerjoo Desk settings.' : 'Hubungkan mailbox (IMAP/SMTP) dari pengaturan Kerjoo Desk.'
    },
    {
      title: isLangEN ? 'Bring in your team' : 'Ajak tim',
      text: isLangEN ? 'Kerjoo admins open it straight from Kerjoo; invite agents from Kerjoo Desk.' : 'Admin Kerjoo langsung masuk dari Kerjoo; agent diundang dari Kerjoo Desk.'
    }
  ]

  const linked = status?.linked
  // koneksi baru diizinkan setelah data contoh (trial) dikosongkan
  const isTemporary = !!redux.isTemporary
  const canConnect = !isTemporary && (linked || (subdomain.length >= 3 && availability?.available !== false))
  const hint = availability
    ? (availability.available ? (isLangEN ? 'Available' : 'Tersedia') : availability.reason)
    : (isLangEN ? 'Cannot be changed later. Lowercase letters, numbers, and hyphens.' : 'Tidak bisa diganti setelah dibuat. Huruf kecil, angka, dan tanda hubung.')
  const hintColor = availability ? (availability.available ? c.success : c.danger) : c.muted

  const renderAction = () => {
    if (!status) return <div style={{ padding: '12px 0' }}>{SpinnerButton}</div>

    if (status.is_connected) {
      return (
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: c.successSoft, color: c.success, padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
            <FontAwesomeIcon icon={faCheckCircle} />
            {isLangEN
              ? `Connected since ${displayDate(status.connected_at, 'DD MMMM YYYY')}`
              : `Terhubung sejak ${displayDate(status.connected_at, 'DD MMMM YYYY')}`}
          </div>
          <div className='d-flex flex-wrap align-items-center' style={{ gap: 16 }}>
            <button disabled={loadingSso} onClick={handleSso} className='btn btn-primary' style={{ borderRadius: 10, padding: '11px 24px', fontWeight: 600 }}>
              {loadingSso && SpinnerButton} {isLangEN ? 'Open Kerjoo Desk' : 'Buka Kerjoo Desk'} <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginLeft: 6, fontSize: 12 }} />
            </button>
            {status.subdomain && <span style={{ fontFamily: 'monospace', fontSize: 14, color: c.muted }}>{status.subdomain}.{DESK_DOMAIN}</span>}
          </div>
          <p style={{ color: c.muted, fontSize: 13, margin: '14px 0 0', lineHeight: 1.6 }}>
            {isLangEN
              ? 'Other Kerjoo admins and sub-admins can open Kerjoo Desk from here too. They are added as Admin the first time they open it.'
              : 'Admin dan sub-admin Kerjoo lain juga bisa membuka Kerjoo Desk dari sini. Mereka otomatis ditambahkan sebagai Admin saat pertama kali membuka.'}
          </p>
        </div>
      )
    }

    return (
      <div>
        {linked
          ? (
            <p style={{ color: c.muted, marginBottom: 16, lineHeight: 1.6 }}>
              {isLangEN ? 'Previously connected to ' : 'Sebelumnya terhubung ke '}
              <b style={{ color: c.text, fontFamily: 'monospace' }}>{linked.subdomain}.{DESK_DOMAIN}</b>.{' '}
              {isLangEN ? 'Your data there is still intact.' : 'Data Anda di sana masih utuh.'}
            </p>
            )
          : (
            <div style={{ marginBottom: 16, maxWidth: 440 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: c.text, marginBottom: 6 }}>{isLangEN ? 'Your Kerjoo Desk address' : 'Alamat Kerjoo Desk Anda'}</label>
              <div style={{ display: 'flex', alignItems: 'center', background: c.input, border: `1px solid ${availability && !availability.available ? c.danger : c.heroBorder}`, borderRadius: 10, overflow: 'hidden' }}>
                <input
                  value={subdomain}
                  maxLength={32}
                  onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: c.text, fontFamily: 'monospace', fontSize: 15, padding: '10px 12px' }}
                />
                <span style={{ color: c.muted, fontFamily: 'monospace', fontSize: 14, padding: '10px 12px 10px 0', whiteSpace: 'nowrap' }}>.{DESK_DOMAIN}</span>
              </div>
              <small style={{ display: 'block', marginTop: 6, color: hintColor }}>{hint}</small>
            </div>
            )}
        <button disabled={loading || !canConnect} onClick={handleConnect} className='btn btn-primary' style={{ borderRadius: 10, padding: '11px 24px', fontWeight: 600, ...(isTemporary && blockedStyle) }}>
          {loading && SpinnerButton} {linked ? (isLangEN ? 'Reconnect' : 'Sambungkan Kembali') : (isLangEN ? 'Start Connection' : 'Mulai Koneksi')}
          {!loading && <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8, fontSize: 12 }} />}
        </button>
        {isTemporary && (
          <p style={{ color: c.muted, fontSize: 13, margin: '12px 0 0' }}>
            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 6 }} />{LangResetDataFirst}
          </p>
        )}
        <p style={{ color: c.muted, fontSize: 13, margin: '12px 0 0' }}>
          {isLangEN
            ? 'You become the Admin of Kerjoo Desk. Other Kerjoo admins and sub-admins are added automatically when they open it.'
            : 'Anda menjadi Admin di Kerjoo Desk. Admin dan sub-admin Kerjoo lain otomatis ditambahkan saat membukanya.'}
        </p>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ background: c.heroBg, border: `1px solid ${c.heroBorder}`, borderRadius: 18, padding: '32px 28px', marginBottom: 24 }}>
        <div className='row align-items-center'>
          <div className='col-lg-7 mb-4 mb-lg-0'>
            <img src={dark ? DeskLogoDark : DeskLogo} alt='Kerjoo Desk' style={{ height: 34, marginBottom: 20 }} />
            <h4 style={{ fontWeight: 800, fontSize: 26, lineHeight: 1.3, color: c.text, marginBottom: 10 }}>
              {isLangEN ? 'Handle customer emails together, as a team' : 'Tangani email pelanggan bersama tim'}
            </h4>
            <p style={{ color: c.muted, fontSize: 15, lineHeight: 1.7, marginBottom: 24, maxWidth: 520 }}>
              {isLangEN
                ? 'A free shared-email helpdesk. Connect once, then open Kerjoo Desk straight from Kerjoo without logging in again.'
                : 'Helpdesk email bersama yang gratis. Hubungkan sekali, lalu buka Kerjoo Desk langsung dari Kerjoo tanpa login ulang.'}
            </p>
            {renderAction()}
            {error && <p style={{ color: c.danger, margin: '12px 0 0', fontSize: 14 }}>{error}</p>}
          </div>
          <div className='col-lg-5'>
            <InboxPreview c={c} />
          </div>
        </div>
      </div>

      <div style={{ background: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: 14, padding: '24px 28px 8px' }}>
        <div className='row'>
          {features.map((f, i) => (
            <div key={i} className='col-md-6 mb-4'>
              <div className='d-flex align-items-center' style={{ gap: 10, marginBottom: 6 }}>
                <FontAwesomeIcon icon={f.icon} style={{ color: c.accent, fontSize: 14, width: 16 }} />
                <span style={{ fontWeight: 700, fontSize: 15, color: c.text }}>{f.title}</span>
              </div>
              <div style={{ fontSize: 13, color: c.muted, lineHeight: 1.6, paddingLeft: 26 }}>{f.text}</div>
            </div>
          ))}
        </div>
      </div>

      {status && !status.is_connected && !linked && (
        <div style={{ background: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: 14, padding: '20px 28px', marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: c.text, marginBottom: 16 }}>{isLangEN ? 'How to get started' : 'Cara memulai'}</div>
          <div className='row'>
            {steps.map((s, i) => (
              <div key={i} className='col-md-4 mb-3 mb-md-0'>
                <div className='d-flex align-items-start' style={{ gap: 12 }}>
                  <span style={{ width: 28, height: 28, borderRadius: '50%', background: c.accent, color: '#fff', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: c.text, marginBottom: 4 }}>{s.title}</div>
                    <div style={{ fontSize: 13, color: c.muted, lineHeight: 1.6 }}>{s.text}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default connect(mapStateToProps)(DeveloperKerjooDeskComponent)
